import { Box, Text } from '@wix/design-system';
import { GtmEvents, useGtmLogger } from '@wix/package-picker-shared';
import {
  ECLIPSE_BG,
  EclipseCta,
  NonBlockerWrapper,
  PageDisclaimer,
  SelectPlanProvider,
  TrustBuilders,
  useAnonymous,
  useInitLayoutBiEvents,
  useInitSelectPlanPageBiEvents,
  useEclipseAiContent,
  useAiRevealController,
  useRedesignedDisclaimers,
} from '@wix/package-picker-shared-eclipse';
import React from 'react';
import { ProductCards } from '../ProductCards';
import { AiAnalyzingIndicator } from '../AiAnalyzingIndicator';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { Background } from '../Background';
import { AllPlansIncludedFeaturesBanner } from '../AllPlansIncludedFeaturesBanner';
import { NavigateBack } from '../NavigateBack';
import { PageTitle as DefaultPageTitle } from '../PageTitle';
import { PlansSectionSkeleton } from './PlansSectionSkeleton';

export interface PlansSectionProps {
  /** Custom page title. If not provided, the default PageTitle component is used. */
  PageTitle?: React.ReactNode;
  /** Custom disclaimer. If not provided, the default PageDisclaimer component is used. */
  Disclaimer?: React.ReactNode;
  /** Whether to show the "Compare features" button. Defaults to true. */
  showCompareButton?: boolean;
  /** Whether to show the TrustBuilders section. Defaults to true. */
  showTrustBuilders?: boolean;
  /** Ref to the FeaturesGrid for scroll-to-compare behavior. */
  gridRef?: React.RefObject<HTMLDivElement>;
  /** Called when a plan is selected. If provided, replaces default navigation to cycle page. */
  onSelectPlan?: (productId: string) => void;
  /** Whether to show the back button. Defaults to false. */
  showBackButton?: boolean;
  /** Custom back button handler. Required when showBackButton is true. */
  onBack?: () => void;
  /** Custom feature names for the "all plans included" banner. Overrides the API-fetched list. */
  allPlansIncludedFeatures?: string[];
}

export const PlansSection = ({
  PageTitle,
  Disclaimer,
  showCompareButton = true,
  showTrustBuilders = true,
  gridRef,
  onSelectPlan,
  showBackButton = false,
  onBack,
  allPlansIncludedFeatures,
}: PlansSectionProps) => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const { isAnonymous, buildSignInRedirectionLink } = useAnonymous();
  const didReportLoad = React.useRef(false);
  useGtmLogger(GtmEvents.SELECT_PLAN);
  const { didInit } = useInitSelectPlanPageBiEvents();
  const { isLoading: isAiContentLoading } = useEclipseAiContent();
  useAiRevealController();
  const isRedesignEnabled = useRedesignedDisclaimers();
  useInitLayoutBiEvents();

  React.useEffect(() => {
    if (didInit && !didReportLoad.current) {
      EclipsePackagePickerModule.internals.bi.onSelectPlanPageLoad?.({});
      didReportLoad.current = true;
    }
  }, [didInit]);

  if (isAiContentLoading) {
    return <PlansSectionSkeleton showAiTitle />;
  }

  return (
    <SelectPlanProvider onSelectPlan={onSelectPlan}>
      <Background
        backgroundColor={ECLIPSE_BG}
        paddingBottom={isRedesignEnabled ? 'SP4' : undefined}
      >
        <Box width="100%" direction="horizontal" verticalAlign="middle">
          {showBackButton && onBack && (
            <NavigateBack
              onBack={onBack}
              backButtonText={t('premium-purchase-plan.eclipse.back.cta')}
              verticalCenter
            />
          )}
          <Box flex="1" align="center">
            {PageTitle !== undefined ? PageTitle : <DefaultPageTitle />}
          </Box>
        </Box>
        {isAnonymous && (
          <Box
            width="60%"
            maxWidth="740px"
            align="center"
            textAlign="center"
            marginTop="SP3"
            margin="0px auto"
          >
            <Text size="medium">
              {t('premium-purchase-plan.anonymous.header.subtitle')}
            </Text>
          </Box>
        )}
        <Box width="100%" marginTop="SP3">
          <NonBlockerWrapper componentName="AllPlansIncludedFeaturesBanner">
            <AllPlansIncludedFeaturesBanner
              features={allPlansIncludedFeatures}
            />
          </NonBlockerWrapper>
        </Box>
        {isAnonymous && (
          <Box marginTop="SP5" align="center">
            <EclipseCta
              variant="primary"
              onClick={() => {
                window.location.href = buildSignInRedirectionLink();
              }}
            >
              {t('premium-purchase-plan.anonymous.header.cta')}
            </EclipseCta>
          </Box>
        )}
        <Box marginTop="30px" marginLeft="SP3" marginRight="SP3">
          <ProductCards />
        </Box>
        {showCompareButton && (
          <Box marginTop="SP5" align="center">
            <EclipseCta
              variant="secondary"
              onClick={() => {
                EclipsePackagePickerModule.internals.bi.onComparePlanFeaturesClick?.(
                  {},
                );
                gridRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('premium-purchase-plan.cards.compare_features.cta')}
            </EclipseCta>
          </Box>
        )}
        <Box
          width="100%"
          maxWidth="1000px"
          align="center"
          textAlign="center"
          marginTop="SP9"
          marginLeft="auto"
          marginRight="auto"
        >
          {Disclaimer !== undefined ? Disclaimer : <PageDisclaimer />}
        </Box>
        {showTrustBuilders && (
          <Box width="100%" align="center" marginTop="SP10">
            <TrustBuilders />
          </Box>
        )}
        <AiAnalyzingIndicator />
      </Background>
    </SelectPlanProvider>
  );
};
